import { ApolloClient, ApolloLink, InMemoryCache, from, split } from '@apollo/client';
import type { KeyFieldsContext } from '@apollo/client/cache/inmemory/policies';
import { HttpLink } from '@apollo/client/link/http';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { StoreObject, getMainDefinition } from '@apollo/client/utilities';
import { isEmpty, isNil } from 'lodash';
import possibleTypes from '@shared/api/possibleTypes.json';
import { ConnectivityNotifier } from '@shared/scenes/connection/connectivity';
import { MBSentry } from '@shared/util/sentry';
import { createWebSocketLink } from './client/WebSocketLink';
import {
  authLink,
  customRequestHeadersLink,
  errorLoggerLink,
  removeCustomCacheKeyLink,
  removeTypenameFromMutationLink,
  stampWorkflowTemplateIdLink,
} from './customApolloLinks';
import {
  createAgentThreadsFieldPolicy,
  createListWorkflowEntitiesFieldPolicy,
  createWorkflowComponentTypePolicies,
  dedupeAndAdd,
} from './fieldpolicy';

/*
    In shared components, we want to be able to run GQL queries that
    aren't attached directly to components. In web/native we only
    rely on react-apollo's HOCs for mutations and queries. This makes
    reusability hard. Therefore we expose the actual apollo client
    to be used in dispatch actions.
*/
let apolloClient: ApolloClient<unknown> | undefined = undefined;

export let webSocketLink: GraphQLWsLink | undefined = undefined;

export function reconnectWebsocket() {
  webSocketLink?.client.terminate(); // non-fatal closing will attempt to be re-opened
}

/*
    To use the apollo client, we need to get it supplied by where it
    is configured in web/native. We add auth headers etc there and
    want to use the exact same client as is being used there. This
    semi-hack is a way to "send" that client object over into the
    shared component world so that we can use it freely here.
    Note: this is expected to be called before apolloClient (above)
    is used by shared components.
*/
export function setSharedApolloClient(client: ApolloClient<unknown>) {
  apolloClient = client;
}

export function getSharedApolloClient(): ApolloClient<unknown> {
  if (!apolloClient) {
    throw new Error('Apollo client not initialized');
  }
  return apolloClient;
}

/**
 * Component types normalized with composite keys using workflowTemplateId + id.
 * Single source of truth is WORKFLOW_COMPONENT_TYPENAMES.
 */

export function createApolloClient(
  responseHeadersLinks: ApolloLink[] = [],
  connectivityNotifier: ConnectivityNotifier,
  customRequestHeaders: () => Promise<Record<string, string>> = async () => ({}),
): ApolloClient<unknown> {
  const queryLoggerLink = new ApolloLink((operation, forward) => {
    MBSentry.breadcrumbFromApolloOperation(operation);
    return forward(operation);
  });

  webSocketLink = createWebSocketLink(connectivityNotifier);

  const fetchNotifyOffline = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const p = fetch(input, init);
    p.catch(reason => {
      if (reason.name !== 'AbortError') {
        connectivityNotifier.offline('fetch::error');
      }
    });
    return p;
  };

  const uri = `${import.meta.env.VITE_API_URL}/api/graphql`;
  const httpLink = new HttpLink({ uri, fetch: fetchNotifyOffline });

  // Split queries based on if it's a subscription or not.
  // This is copy pasted from the Apollo docs:
  // https://www.apollographql.com/docs/react/advanced/subscriptions/#client-setup
  const dataLink = split(
    // split based on operation type
    operation => {
      const def = getMainDefinition(operation.query);
      return def.kind === 'OperationDefinition' && def.operation === 'subscription';
    },
    webSocketLink,
    httpLink,
  );

  apolloClient = new ApolloClient({
    link: from([
      queryLoggerLink,
      customRequestHeadersLink(customRequestHeaders),
      authLink(),
      // Note(fant): this error link needs to be before the data link
      // https://stackoverflow.com/a/55169771
      errorLoggerLink,
      removeTypenameFromMutationLink,
      removeCustomCacheKeyLink,
      stampWorkflowTemplateIdLink,
      ...responseHeadersLinks,
      dataLink,
    ]),
    assumeImmutableResults: true,
    cache: new InMemoryCache({
      possibleTypes,
      // https://www.apollographql.com/docs/react/local-state/managing-state-with-field-policies/
      typePolicies: {
        Channel: {
          keyFields: (obj: Readonly<StoreObject>, context: Readonly<KeyFieldsContext>) => {
            if (!isNil(obj.slug)) {
              return `${context.typename}:${obj.slug}`;
            }
            if (import.meta.env.DEV) {
              console.warn(
                'Channel object missing `slug` for cache key. Ensure the query includes `slug`.',
              );
            }
            return false;
          },
          fields: {
            name: {
              merge(existing, incoming, options) {
                return isNil(existing) || !isCoreApiGetChannelQuery(options.variables)
                  ? incoming
                  : existing;
              },
            },
            bundle: {
              merge(existing, incoming) {
                // Fall back to null so the field always exists in the cache record;
                // returning undefined drops it and makes every ChannelSettings read
                // incomplete, which blocks watcher updates (optimistic ones included).
                return incoming ?? existing ?? null;
              },
            },
          },
        },
        Business: {
          fields: {
            // img and imageUrl are aliases; preserve the cached value when a
            // query omits one of them so reads of either field stay populated.
            // Note: Apollo passes no `from` to readField inside merge functions
            // (see policies.js), so reading the sibling field here always
            // returns undefined and only emits a "Undefined 'from'" warning —
            // hence the plain `incoming ?? existing` fallback.
            img: {
              merge(existing, incoming) {
                return incoming ?? existing ?? null;
              },
            },
            imageUrl: {
              merge(existing, incoming) {
                return incoming ?? existing ?? null;
              },
            },
          },
        },
        ViewTemplate: {
          keyFields: ['workflowTemplateId', 'id'],
        },
        // BusinessMembership has no `id` field; businessId + userId is a natural
        // composite key (one membership per user per business), so this normalizes
        // as a real cache entity instead of an unmergeable embedded object.
        BusinessMembership: {
          keyFields: ['businessId', 'userId'],
        },
        Query: {
          fields: {
            listAgentThreads: createAgentThreadsFieldPolicy(),
            listApiKeys: {
              keyArgs: false,
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'apiKeys',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listViewTemplatesV2: {
              keyArgs: [
                'input',
                ['workflowTemplateId', 'type', 'subtype', 'isPublic', 'name', 'excludeSubtypes'],
              ],
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'viewTemplates',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listReportsV2: {
              keyArgs: (args: Record<string, any> | null) => {
                const workflowTemplateIds = args?.input?.workflowTemplateIds;
                // Cache all reports together when not filtering by workflowTemplateIds
                if (isNil(workflowTemplateIds) || isEmpty(workflowTemplateIds)) {
                  return false;
                }
                // Cache separately when filtering by workflowTemplateIds
                return ['input', ['workflowTemplateIds']];
              },
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'reports',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listPublicBundleListings: {
              keyArgs: ['input', ['query', 'tags']],
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'bundleListings',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listFileOperations: {
              keyArgs: false,
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'items',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listWorkflowTemplateAutomations: {
              keyArgs: ['input', ['workflowTemplateId', 'trigger', 'name']],
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'automations',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listBusinesses: {
              keyArgs: ['input', ['name']],
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'businesses',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listUsers: {
              keyArgs: (args: Record<string, any> | null, context: any) => {
                const customCacheKey = context.variables?.customCacheKey;
                if (!isNil(args) && !isEmpty(customCacheKey)) {
                  args.customCacheKey = customCacheKey;
                  return ['customCacheKey'];
                }
                return [
                  'input',
                  [
                    'ids',
                    'name',
                    'username',
                    'isActive',
                    'isDeactivated',
                    'orderBys',
                    'excludeSystemUsers',
                    'memberOfChannelIds',
                    'notMemberOfChannelIds',
                  ],
                ];
              },
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'users',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listChannelMembershipsV2: {
              // Keyed by a caller-supplied customCacheKey (via @connection) so this
              // accumulated entry is scoped to its consumer (e.g. the channel member
              // list) rather than colliding with any other membership query for the
              // same channel. userIds is therefore excluded from the key: memberships
              // are fetched in batches by userId (the backend caps a request at 50
              // ids) and accumulated into a single entry, deduped by membership id,
              // so the full set builds up across batches.
              keyArgs: (args: Record<string, any> | null, context: any) => {
                const customCacheKey = context.variables?.customCacheKey;
                if (!isNil(args) && !isEmpty(customCacheKey)) {
                  args.customCacheKey = customCacheKey;
                  return ['customCacheKey'];
                }
                return ['input', ['channelId', 'userIds', 'onlyAssignableUsers']];
              },
              merge(existing, incoming, { readField, mergeObjects }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'memberships',
                  readField,
                  mergeObjects,
                  // No offset pagination here: every batch is an append, so we treat
                  // each response as a continuation whenever we already have data.
                  offset: existing ? 1 : 0,
                });
              },
            },
            listBusinessMemberships: {
              // Same shape as listChannelMembershipsV2: keyed by a caller-supplied
              // customCacheKey so this accumulated entry is scoped to its consumer,
              // with userIds excluded from the key since memberships are fetched in
              // batches by userId and accumulated into a single entry, deduped by
              // userId (BusinessMembership has no `id`; see the BusinessMembership
              // keyFields above), so the full set builds up across batches.
              keyArgs: (args: Record<string, any> | null, context: any) => {
                const customCacheKey = context.variables?.customCacheKey;
                if (!isNil(args) && !isEmpty(customCacheKey)) {
                  args.customCacheKey = customCacheKey;
                  return ['customCacheKey'];
                }
                return ['input', ['role']];
              },
              merge(existing, incoming, { readField, mergeObjects }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'businessMemberships',
                  readField,
                  mergeObjects,
                  idField: 'userId',
                  // No offset pagination here: every batch is an append, so we treat
                  // each response as a continuation whenever we already have data.
                  offset: existing ? 1 : 0,
                });
              },
            },
            listWorkspaceSections: {
              keyArgs: false,
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'channelAssociations',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listUserGroups: {
              keyArgs: ['input', ['ids', 'name']],
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'userGroups',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listBookmarks: {
              keyArgs: ['input', ['name']],
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'bookmarks',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listUserGroupUserAssociations: {
              keyArgs: ['input', ['userGroupId', 'userFirstOrLastName']],
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'userGroupUserAssociations',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listWorkflowEntities: createListWorkflowEntitiesFieldPolicy(),
            listWorkflowTemplatesV2: {
              keyArgs: [
                'input',
                [
                  'isPublic',
                  'types',
                  'parentTemplateId',
                  'rootTemplateId',
                  'ids',
                  'name',
                  'relatesToWorkflowTemplateId',
                ],
              ],
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'workflowTemplates',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            // Embedded legacy payload: merge so refetches / partial writes never drop snoozeUntil.
            legacyGetNotificationSettings: {
              merge(existing, incoming, { mergeObjects }) {
                if (!incoming) {
                  return existing;
                }
                if (!existing) {
                  return incoming;
                }
                return mergeObjects(existing, incoming);
              },
            },
          },
        },
        ...createWorkflowComponentTypePolicies(possibleTypes.AnyComponent),
        // Disable caching for TodoComponentDataValue to avoid Apollo merging
        // todo items with the same id across different audit trail entries
        TodoComponentDataValue: {
          keyFields: false,
        },
      },
    }),
    queryDeduplication: true,
    defaultOptions: {
      query: { fetchPolicy: 'cache-first' },
    },
  });

  return apolloClient;
}

function isCoreApiGetChannelQuery(variables?: Record<string, any>): boolean {
  if (isNil(variables)) {
    return false;
  }
  const keys = Object.keys(variables);
  if (keys.length === 2 && keys.includes('slug') && keys.includes('id')) {
    return true;
  }
  return false;
}
